/* eslint class-methods-use-this: 0, no-param-reassign: 0, no-async-promise-executor: 0 */
import { CancelToken } from 'axios';
import { DirectoryGroupingConsts, ExportFileTypes } from 'client/util/Constants';
import {
  cloneDeep,
  difference,
  extend,
  find,
  get,
  includes,
  isEqual,
  merge,
} from 'lodash';
import { parallelLimit } from 'async';
import BoundItemsApi from 'client/api/v2/BoundItemsApi';
import DirectoryGroupingApi from 'client/api/v2/DirectoryGroupingApi';
import DownloadUtil from 'client/util/DownloadUtil';
import JobUtils from 'client/util/JobUtils';
import Vue from 'vue';
import moment from 'moment';

/**
 * The structure the vuex store follows the Redux Normalizing State Shape pattern found here
 * https://redux.js.org/recipes/structuring-reducers/normalizing-state-shape
 */

// ----------------------------------------------------------------------------
// Private Helper Methods
// ----------------------------------------------------------------------------

const MONGO_ID = '_id';

const addBoundItem = (boundItems = {}, item = {}) => {
  if (!boundItems.byId[item.id]) {
    Vue.set(boundItems.byId, item.id, item);
    boundItems.totalCount += 1;
  } else {
    boundItems.byId[item.id] = extend(boundItems.byId[item.id], item);
  }
};

const addItem = (items = [], id = '') => {
  if (items.indexOf(id) === -1) {
    items.push(id);
  }
};

const removeBoundItem = (boundItems = {}, id = '') => {
  if (boundItems.byId[id]) {
    delete boundItems.byId[id];
  }

  if (boundItems.totalCount > 0) {
    boundItems.totalCount -= 1;
  }
};

const removeItem = (items = [], id = '') => {
  const index = items.indexOf(id);

  if (index !== -1) {
    items.splice(index, 1);
  }
};

/**
 * Keep id and _id in sync in case one is missing
 */
const syncIdField = (item = {}, idField = '') => {
  const id = get(item, idField);
  if (idField === MONGO_ID) {
    if (!item.id && id) {
      item.id = id;
    } else if (!item[MONGO_ID] && item.id) {
      item[MONGO_ID] = item.id;
    }
  }
};

/**
 * @class The CollectionModule class provides an extensible base class for use
 * as a Vuex store module. It contains actions and getters used by the
 * ItemTable component for the data it displays. The CollectionModule class can
 * be extended if needed to override common behavior. An example would be
 * syncing modified items with the set of items retrieved in a call to
 * fetchCollection(). Any extensions such as this must be done _before_ calling
 * extractModule() which returns the singleton object suitable for use as a
 * Vuex store module. Because the singleton object is returned from a class
 * method, it has access to all methods the CollectionModule instance that
 * call it. This is what allows derived classes to override methods provided by
 * the CollectionModule class.
 * @todo
 * 1) Provide an example of use
 * 2) Rename actions, getters and mutations that use the word "collections" to
 *    use the word "items". e.g. SET_COLLECTION -> SET_ITEMS, RESET_COLLECTION
 *    -> RESET_ITEMS, resetCollection() -> resetItems(), ...
 * 3) Always pass objects to mutations for consistent method signatures.
 * 4) Consider breaking this file out into individual files for actions,
 *    getters and mutations files. It's getting a little long.
 * 5) Keep track of item binds in either boundItems or items, but not both!
 */
export default class CollectionModule {
  constructor(Api = {
    axios: {},
    baseUrl: '',
    defaultParams: {
      sort: '',
    },
    maxFetchLimit: Number(0),
  }, idField, sortBy = '', BoundApi = new BoundItemsApi()) {
    this.Api = Api;
    this.boundItemsApi = BoundApi;
    this.idField = idField;
    this.sortBy = sortBy || (Api && Api.defaultParams && Api.defaultParams.sort);
  }

  extendExistingObject(newObject, originalObject) {
    return extend({}, originalObject || {}, newObject);
  }

  /**
   * @todo Use ES7 decorators to mark methods as an action, getter or mutation,
   * so that they don't need to be manually added to the Vuex store module
   * definition as done below. When using an ES7 decorator the decorated method
   * can likely be _added_ to the actions, getters, or mutations hashes which
   * can then be referenced here!
   */
  extractModule() {
    return {
      namespaced: true,
      state: this.getInitialState(),
      actions: {
        createDirectBinding: (...params) => this.createDirectBinding(...params),
        checkForBoundItemsByIds: (...params) => this.checkForBoundItemsByIds(...params),
        deleteAllFilters: (...params) => this.deleteAllFilters(...params),
        deleteFilter: (...params) => this.deleteFilter(...params),
        deleteFilters: (...params) => this.deleteFilters(...params),
        deleteItems: (...params) => this.deleteItems(...params),
        exportData: (...params) => this.exportData(...params),
        fetchAll: (...params) => this.fetchAll(...params),
        fetchCollection: (...params) => this.fetchCollection(...params),
        fetchItem: (...params) => this.fetchItem(...params),
        processRemoveIndirectBindings: (...params) => this.processRemoveIndirectBindings(...params),
        refreshBoundItems: (...params) => this.refreshBoundItems(...params),
        refreshCollection: (...params) => this.refreshCollection(...params),
        removeIndirectBindings: (...params) => this.removeIndirectBindings(...params),
        removeMultiIndirectBindings: (...params) => this.removeMultiIndirectBindings(...params),
        removePreviouslySelectedItem: (...params) => this.removePreviouslySelectedItem(...params),
        restorePreviousDirectBinding: (...params) => this.restorePreviousDirectBinding(...params),
        resetCollection: (...params) => this.resetCollection(...params),
        resetDeletedItemIds: (...params) => this.resetDeletedItemIds(...params),
        selectAllItems: (...params) => this.selectAllItems(...params),
        selectItem: (...params) => this.selectItem(...params),
        setBoundItemsUrl: (...params) => this.setBoundItemsUrl(...params),
        setDeletedItemIds: (...params) => this.setDeletedItemIds(...params),
        setShowBoundItems: (...params) => this.setShowBoundItems(...params),
        setFilter: (...params) => this.setFilter(...params),
        setFilters: (...params) => this.setFilters(...params),
        setPage: (...params) => this.setPage(...params),
        setSearchTerm: (...params) => this.setSearchTerm(...params),
        setSortField: (...params) => this.setSortField(...params),
        showAllItems: (...params) => this.showAllItems(...params),
        showBoundItems: (...params) => this.showBoundItems(...params),
        syncItem: (...params) => this.syncItem(...params),
        unselectAllItems: (...params) => this.unselectAllItems(...params),
        unselectItem: (...params) => this.unselectItem(...params),
        unselectItems: (...params) => this.unselectItems(...params),
        updateItem: (...params) => this.updateItem(...params),
        updateItems: (...params) => this.updateItems(...params),
      },
      getters: {
        areAllItemsSelected: (...params) => this.areAllItemsSelected(...params),
        areOnlySomeItemsSelected: (...params) => this.areOnlySomeItemsSelected(...params),
        areShowingBoundItems: state => this.areShowingBoundItems(state),
        canRefreshCollection: (...params) => this.canRefreshCollection(...params),
        getAppliedFilters: (...params) => this.getAppliedFilters(...params),
        getBoundItemsTotalCount: state => this.getBoundItemsTotalCount(state),
        getBoundItem: (...params) => this.getBoundItem(...params),
        getCollection: state => this.getCollection(state),
        getDisplayName: (...params) => this.getDisplayName(...params),
        getFilters: (...params) => this.getFilters(...params),
        getFirstAndLastName: state => this.getFirstAndLastName(state),
        getItem: state => this.getItem(state),
        getItemPaths: state => this.getItemPaths(state),
        getItemsToBind: state => this.getItemsToBind(state),
        getItemsToUnbind: state => this.getItemsToUnbind(state),
        getMaxNumBoundItems: state => this.getMaxNumBoundItems(state),
        getNextPageToken: state => this.getNextPageToken(state),
        getPage: state => this.getPage(state),
        getRowsPerPage: state => this.getRowsPerPage(state),
        getSkipToken: state => this.getSkipToken(state),
        getPathInfoForParent: (...params) => this.getPathInfoForParent(...params),
        getPathInfoForTarget: (...params) => this.getPathInfoForTarget(...params),
        getSearchTerm: state => this.getSearchTerm(state),
        getPreviouslyDirectlyBoundItems: state => this.getPreviouslyDirectlyBoundItems(state),
        getPreviouslyDirectlyBoundItemsCount: state => this
          .getPreviouslyDirectlyBoundItemsCount(state),
        getSearchField: (...params) => this.getSearchField(...params),
        getSelectedItems: state => this.getSelectedItems(state),
        getSelectedItemsCount: state => this.getSelectedItemsCount(state),
        getSelectedItemsIndirectlyBoundByGroup: (...params) => this
          .getSelectedItemsIndirectlyBoundByGroup(...params),
        getSortField: state => this.getSortField(state),
        getTotalCount: state => this.getTotalCount(state),
        getTotalCountOfAllItems: state => this.getTotalCountOfAllItems(state),
        getTotalCountOfActiveItems: state => this.getTotalCountOfActiveItems(state),
        getGroupMembership: (...params) => this.getGroupMembership(...params),
        isItemDirectlyBound: state => this.isItemDirectlyBound(state),
        isItemIndirectlyBound: (...params) => this.isItemIndirectlyBound(...params),
        isItemPreviouslyDirectlyBound: (...params) => this.isItemPreviouslyDirectlyBound(...params),
        isItemPreviouslySelected: (...params) => this.isItemPreviouslySelected(...params),
        isItemSelectable: (...params) => this.isItemSelectable(...params),
        isItemSelected: (...params) => this.isItemSelected(...params),
      },
      mutations: {
        ADD_BOUND_ITEM: (...params) => this.ADD_BOUND_ITEM(...params),
        ADD_BOUND_ITEMS: (...params) => this.ADD_BOUND_ITEMS(...params),
        BOUND_ITEMS_HAVE_BEEN_FETCHED: (...params) => this.BOUND_ITEMS_HAVE_BEEN_FETCHED(...params),
        BOUND_ITEMS_TOTAL_COUNT_HAS_BEEN_SET: (...params) => this
          .BOUND_ITEMS_TOTAL_COUNT_HAS_BEEN_SET(...params),
        DELETE_ALL_FILTERS: (...params) => this.DELETE_ALL_FILTERS(...params),
        DELETE_FILTER: (...params) => this.DELETE_FILTER(...params),
        DELETE_FILTERS: (...params) => this.DELETE_FILTERS(...params),
        DELETE_ITEMS: (...params) => this.DELETE_ITEMS(...params),
        REFRESH_BOUND_ITEMS: (...params) => this.REFRESH_BOUND_ITEMS(...params),
        REFRESH_COLLECTION: (...params) => this.REFRESH_COLLECTION(...params),
        REMOVE_BOUND_ITEM: (...params) => this.REMOVE_BOUND_ITEM(...params),
        REMOVE_BOUND_ITEMS: (...params) => this.REMOVE_BOUND_ITEMS(...params),
        REMOVE_ITEM_DIRECT_BINDING: (...params) => this.REMOVE_ITEM_DIRECT_BINDING(...params),
        REMOVE_ITEM_PATHS: (...params) => this.REMOVE_ITEM_PATHS(...params),
        REMOVE_PREVIOUSLY_SELECTED_ITEM: (...params) => this
          .REMOVE_PREVIOUSLY_SELECTED_ITEM(...params),
        RESET_COLLECTION: (...params) => this.RESET_COLLECTION(...params),
        RESET_DELETED_ITEM_IDS: (...params) => this.RESET_DELETED_ITEM_IDS(...params),
        RESTORE_PREVIOUS_DIRECT_BINDING: (...params) => this
          .RESTORE_PREVIOUS_DIRECT_BINDING(...params),
        SELECT_ITEM: (...params) => this.SELECT_ITEM(...params),
        SELECT_ITEMS: (...params) => this.SELECT_ITEMS(...params),
        SET_BOUND_ITEMS: (...params) => this.SET_BOUND_ITEMS(...params),
        SET_BOUND_ITEMS_URL: (...params) => this.SET_BOUND_ITEMS_URL(...params),
        SET_BOUND_ITEMS_TOTAL_COUNT: (...params) => this.SET_BOUND_ITEMS_TOTAL_COUNT(...params),
        SET_COLLECTION: (...params) => this.SET_COLLECTION(...params),
        SET_DELETED_ITEM_IDS: (...params) => this.SET_DELETED_ITEM_IDS(...params),
        SET_PREVIOUSLY_SELECTED_ITEMS: (...params) => this.SET_PREVIOUSLY_SELECTED_ITEMS(...params),
        SET_FILTER: (...params) => this.SET_FILTER(...params),
        SET_SEARCH_TERM: (...params) => this.SET_SEARCH_TERM(...params),
        SET_SORT_FIELD: (...params) => this.SET_SORT_FIELD(...params),
        SET_PAGINATION_LIMIT: (...params) => this.SET_PAGINATION_LIMIT(...params),
        SET_PAGINATION_SKIP: (...params) => this.SET_PAGINATION_SKIP(...params),
        SET_ITEM: (...params) => this.SET_ITEM(...params),
        SET_ITEM_PATHS: (...params) => this.SET_ITEM_PATHS(...params),
        SET_UNDELETED_ITEM_ID: (...params) => this.SET_UNDELETED_ITEM_ID(...params),
        SHOW_ALL_ITEMS: (...params) => this.SHOW_ALL_ITEMS(...params),
        SHOW_BOUND_ITEMS: (...params) => this.SHOW_BOUND_ITEMS(...params),
        UNSELECT_ITEMS: (...params) => this.UNSELECT_ITEMS(...params),
        UNSELECT_ITEM: (...params) => this.UNSELECT_ITEM(...params),
        UPDATE_ITEM: (...params) => this.UPDATE_ITEM(...params),
      },
    };
  }

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  getInitialState() {
    return {
      entities: {
        boundItems: {
          /**
           * A data table for items that have been selected by the user (and may
           * or may not be visible to the user). The IDs of the items are the keys
           * and the items themselves are the values.
           */
          byId: {},
          totalCount: 0,
        },
        items: {
          /**
           * A data table for items that are visible to the user.
           * The IDs of the items are the keys and the items themselves are
           * the values.
           */
          byId: {},

          /**
           * The total count may not be the total count of all items in the
           * universe. The total count may be the total count of all items that
           * match a search term, when present. Or the total count may be the
           * total count of bound items, when showing bound items.
           */
          totalCount: 0,

          /**
           * The total count of all items is the total count of all items in
           * universe. Search parameters do not affect this number nor does
           * showing only the bound items. The value of this property is set to
           * null initially so that the cases of the property not having been
           * set and having no items in the universe, i.e. set to 0, can be
           * distinguished.
           */
          totalCountOfAllItems: null,
        },
      },
      features: {
        boundItemsUrl: '',
        deletedItems: {
          deletedItemIds: [],
          failedTasks: [],
        },
        hasBoundItemsTotalCountBeenSet: false,
        haveBoundItemsBeenFetched: false,
        items: {
          filters: [],
          ids: [],
          pagination: {
            skip: 0,
            limit: 50,
            nextPageToken: null,
            skipToken: null,
          },
          searchTerm: null,
          sort: this.sortBy,
        },
        maxNumBoundItems: 1000,
        previouslyDirectlyBoundItems: {
          byId: {},
        },
        previouslySelectedItems: {
          ids: [],
        },
        selectedItems: {
          ids: [],
        },
        showBoundItems: false,
        unselectedItems: {
          ids: [],
        },
      },
    };
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  /**
   * Create a direct binding from the target to the parent, then retrieve a
   * fresh set of directly bound items so that the new paths from the target to
   * parent are available, and then add the new path for the direct binding to
   * the store.
   */
  createDirectBinding(context, {
    parentType,
    parentId,
    targetType,
    targetId,
  }) {
    return new Promise((resolve, reject) => {
      const payload = {
        parentType,
        parentId,
        targetType,
        selectedTargetIds: [targetId],
        unselectedTargetIds: [],
      };

      context.dispatch('DirectoryGroupingModule/bindAssociations', payload, { root: true })
        .then(() => DirectoryGroupingApi.getAssociations(parentType, parentId, targetType))
        .then((directlyBoundItems) => {
          if (directlyBoundItems && directlyBoundItems.length) {
            // Find the directly bound item paths from the graph connection
            // for the target item.
            const path = find(
              directlyBoundItems,
              directlyBoundItem => directlyBoundItem.to.id === targetId,
            );

            // Then add the new direct binding path from the item (target) to
            // the parent to the store.
            context.commit('SET_ITEM_PATHS', { id: targetId, path: [path] });
          }
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async checkForBoundItemsByIds(context, ids) {
    const { boundItemsUrl, showBoundItems } = context.state.features;

    const result = await this
      .getApi(showBoundItems)
      .checkForBoundItems(boundItemsUrl, ids);

    return result;
  }

  deleteAllFilters(context, { reset = true } = {}) {
    if (reset) context.commit('RESET_COLLECTION', { soft: true });
    context.commit('DELETE_ALL_FILTERS');
  }

  /*
   * @todo: filterName is not guaranteed to be unique. We should use `type` + `displayValue`
   * e.g. password expiring within 7 vs. 14 days will both use 'password_expiration_date' as name
   */
  deleteFilter(context, { filterName, reset = true }) {
    if (reset) context.commit('RESET_COLLECTION', { soft: true });
    context.commit('DELETE_FILTER', { filterName });
  }

  deleteFilters(context, { filtersArray, reset = true }) {
    if (reset) context.commit('RESET_COLLECTION', { soft: true });
    context.commit('DELETE_FILTERS', { filtersArray });
  }

  deleteItems(context, ids = []) {
    return new Promise((resolve) => {
      const deletedIds = [];
      const failedTasks = [];
      const tasks = [];

      ids.forEach((id) => {
        tasks.push((cb) => {
          this.Api.delete(id)
            .then(() => {
              deletedIds.push(id);
              context.dispatch('ProgressModule/incrementCompleted', {}, { root: true });
              cb();
            })
            .catch((error) => {
              failedTasks.push({ id, error });
              context.dispatch('ProgressModule/incrementCompleted', {}, { root: true });
              cb();
            });
        });
      });

      if (tasks.length) {
        context.dispatch('ProgressModule/updateBulkActionStatus', true, { root: true });

        context.dispatch('ProgressModule/setCounter', {
          completed: 0,
          total: tasks.length,
        }, { root: true });

        parallelLimit(tasks, 5, () => {
          context.dispatch('ProgressModule/setCounter', {
            completed: tasks.length,
            total: tasks.length,
          }, { root: true });

          context.commit('DELETE_ITEMS', deletedIds);

          context.dispatch('ProgressModule/updateBulkActionStatus', false, { root: true });
          resolve({ deletedIds, failedTasks });
        });
      } else {
        resolve();
      }
    });
  }

  // Exports all items into the provided file type
  exportData({ dispatch }, { exportType = ExportFileTypes.json, fileNamePrefix = 'items' } = {}) {
    return new Promise(async (resolve, reject) => {
      // Must pass in valid file type
      if (exportType !== ExportFileTypes.json && exportType !== ExportFileTypes.csv) {
        reject(new Error('Invalid file type'));
        return;
      }

      let data = [];
      try {
        data = await dispatch('fetchAll');
      } catch (err) {
        reject(err);
        return;
      }

      const fileName = `${fileNamePrefix}_${moment().format('YYYYMMDD')}`;
      if (exportType === ExportFileTypes.json) {
        DownloadUtil.downloadJson(data, `${fileName}.json`);
      } else if (exportType === ExportFileTypes.csv) {
        const csv = JobUtils.convertJsonToCsv(data, {
          alphabetize: true,
          sanitize: true,
        });
        DownloadUtil.downloadFile(csv, `${fileName}.csv`);
      }
      resolve(data.length);
    });
  }

  fetchAll() {
    return this.Api.fetchAll();
  }

  fetchCollection(context, { next = false } = {}) {
    return new Promise((resolve, reject) => {
      let { skip } = context.state.features.items.pagination;
      const { sort } = context.state.features.items;
      const { searchTerm } = context.state.features.items;
      const {
        skipToken,
        nextPageToken,
        limit,
      } = context.state.features.items.pagination;
      const filters = context.getters.getFilters;

      if (next === true && !nextPageToken && !skipToken) {
        // nextPageToken is set to null when all results have been fetched
        // skipToken is set to null when all results have been fetched
        if (this.Api.usesPageTokens || this.Api.usesSkipTokens) {
          resolve();
          return;
        }

        skip += limit;

        if (skip >= context.state.entities.items.totalCount) {
          resolve();
          return;
        }

        context.commit('SET_PAGINATION_SKIP', { skip });
      }

      this.fetchWrapper(context, skip, limit, sort, searchTerm, filters, nextPageToken, skipToken)
        .then((items) => {
          const payload = {
            results: items.results,
            totalCount: items.totalCount,
            nextPageToken: items.nextPageToken,
            skipToken: items.skipToken,
          };

          if (context.state.entities.items.totalCountOfAllItems === null) {
            payload.totalCountOfAllItems = items.totalCount;
          }

          context.commit('SET_COLLECTION', payload);

          return items.results.map(result => get(result, this.idField));
        })
        .then((ids) => {
          // If a bound items URL is available and bound items aren't being
          // shown then then get the total count of bound items, if not
          // already available, and then check to see if the next set of
          // ids are bound items. Note that this check isn't required when
          // showing bound items as discussed below.

          if (context.state.features.boundItemsUrl && !context.state.features.showBoundItems) {
            if (!context.state.features.hasBoundItemsTotalCountBeenSet) {
              this.Api
                .getBoundItemCount(context.state.features.boundItemsUrl)
                .then((totalCount) => {
                  context.commit('SET_BOUND_ITEMS_TOTAL_COUNT', { totalCount });
                  context.commit('BOUND_ITEMS_TOTAL_COUNT_HAS_BEEN_SET');

                  // Fetch the bound items initially when there are less than
                  // 1000 so that they can be synced with with the server after
                  // removing an indirect binding. If the bound items were not
                  // available yet, then this sync wouldn't be possible, so
                  // they are fetched here.

                  /**
                   * Zach Smith, HY-2260
                   * For a user bound to a user group, which is bound to a device group,
                   * which is bound to any decent amount of devices, and that user group
                   * has a large JSON attribute, calls to the BoundItemsApi that result in
                   * a response over 4MB will fail due to RPC constraints. This is because
                   * the large JSON attribute will be returned on each item in the BoundItemsApi
                   * response (twice on each result, actually).
                   *
                   * To decrease the amount of failed network calls due to the 4MB limit,
                   * we can prevent the `initializeBoundItems` call from being made
                   * (it has a limit=1000). Instead, the call directly below,
                   * `this.getApi().checkForBoundItems` will only check the previously fetched
                   * page of items to see if any of those are bound, rather than trying to get back
                   * all of the items.
                   *
                   * `context.state.features.usePaginationMethodForBoundDevices` is a state value
                   * set in `SystemCollection.js` that opts in to custom paging functionality for
                   * the "show bound items" view, but we can also hook into it here to prevent the
                   * BoundItemsApi (limit=1000) from being called while paginating thru the
                   * "show all items" view. We can rely on the BoundItemsApi pagination call
                   * below to get the bound statuses of the items that were just paginated.
                   *
                   * More info can be found in comments made in `SystemCollection.js`.
                   */
                  if (
                    !context.state.features.usePaginationMethodForBoundDevices
                    && totalCount < context.state.features.maxNumBoundItems
                  ) {
                    this.initializeBoundItems(context);
                  }
                })
                .catch(() => {
                  // Ignore errors trying to fetch the bound item count.
                });
            }

            // If not showing bound items then check if any of the IDs just
            // retrieved are bound items. If any of the IDs are bound items
            // then the UI will mark them checked in the status column. Note
            // that this check is not necessary when showing bound items
            // since only the bound items are shown.

            this.getApi(context.state.features.showBoundItems)
              .checkForBoundItems(context.state.features.boundItemsUrl, ids)
              .then(({ boundItemIds = [], boundItems }) => {
                if (boundItemIds.length) {
                  // Don't add any bound item IDs that may have been
                  // removed other wise they will be shown as selected
                  // when the admin removes the item in the show all view,
                  // then navigates to the show bound items view, and then
                  // back to the show all view.

                  for (let i = 0; i < boundItemIds.length; i += 1) {
                    const boundItemId = boundItemIds[i];

                    if (context.state.features.unselectedItems.ids.indexOf(boundItemId) !== -1) {
                      boundItemIds.splice(i, 1);
                      i -= 1;
                    }
                  }

                  context.commit('SELECT_ITEMS', { ids: boundItemIds });
                  context.commit('SET_PREVIOUSLY_SELECTED_ITEMS', { ids: boundItemIds });

                  this.processBoundItems(context, boundItems);

                  resolve(ids);
                } else {
                  resolve(ids);
                }
              })
              .catch(() => {
                // Ignore errors checking for bound items and resolve the promise
                // so the caller can continue displaying the ItemTable.
                resolve(ids);
              });
          } else {
            resolve(ids);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * After bound items are retrieved iterate through each one to set the paths
   * to each bound item to its parent.
   *
   * Note that this action is "private". It is not exported in extractModule()
   * since it is not expected to be dispatched by callers. It is meant for
   * internal use by the module class itself.
   * @param context The Vuex context that can be used to commit mutations
   * @param boundItems This method will be passed an array of the bound items.
   */
  processBoundItems(context, boundItems) {
    const { previouslyDirectlyBoundItems } = context.state.features;

    boundItems.forEach((boundItem) => {
      const { id, paths } = boundItem;

      if (paths && paths.length) {
        paths.forEach((path) => {
          if (previouslyDirectlyBoundItems.byId[id]
              && isEqual(previouslyDirectlyBoundItems.byId[id], path)) {
            return;
          }

          context.commit('SET_ITEM_PATHS', { id, path });
        });
      }
    });
  }

  fetchItem(context, itemId) {
    return this.Api
      .get(itemId)
      .then((item) => {
        context.commit('SET_ITEM', item);
      });
  }

  /**
   * Unselect a single indirectly bound item by removing the user from the user
   * group that indirectly bound them to a system.
   */
  removeIndirectBindings({ dispatch, getters }, {
    itemId,
    groupIds,
    groupType,
    memberId,
    memberType,
    unselectUserGroup,
  }) {
    const payloads = groupIds.map(groupId => ({
      groupId,
      groupType,
      operation: DirectoryGroupingConsts.operations.remove,
      memberId,
      memberType,
    }));

    // When unselecting the user group, in addition to the item ID of the item
    // being acted on, we also need to unselect and remove the indirect binding
    // from _other_ selected items that are also indirectly bound by the same
    // group membership.
    //
    // Consider the following use case:
    //
    // Given 5 systems that are indirectly bound to a user through the user's
    // membership in a group. If the first system is unselected then the user's
    // group membership will be removed here and the first system unselected in
    // the UI. But because the user was removed from the user group that
    // provided an indirect binding to the other four systems, they too need to
    // be unselected in the UI. Otherwise if/when the admin user attempted to
    // unselect them, removing the user from the group would fail with a 404
    // error.
    //
    // This is only done when unselecting the user group because in this case
    // removing the member from the group affects other items.

    const itemIds = unselectUserGroup
      ? getters.getSelectedItemsIndirectlyBoundByGroup(groupIds, groupType)
      : [];

    if (itemIds.indexOf(itemId) === -1) {
      itemIds.push(itemId);
    }

    return dispatch('processRemoveIndirectBindings', {
      payloads,
      groupIds,
      itemIds,
      unselectUserGroup,
    });
  }

  /**
   * Remove multiple indirectly bound items by removing the user(s) from the
   * user group(s) that indirectly bound them to the system(s).
   * @param {Object[]} indirectBindings - Array of indirectly bindings to sever
   * @param {string} indirectBindings[].itemId - The ID of the indirectly bound item
   * @param {string[]} indirectBindings[].groupIds - Array of group IDs to remove the member from
   * @param {string} indirectBindings[].groupType - The type of the group
   * @param {string} indirectBindings[].memberId - The ID the member to remove from the group
   * @param {string} indirectBindings[].memberType - The type of the member
   * @param {boolean} unselectUserGroup - Unselect the user groups after removing
   * the members if true
   */
  removeMultiIndirectBindings({ dispatch }, indirectBindings, unselectUserGroup) {
    // First create a hash of member IDs to groups mapping, e.g.:
    //
    // {
    //   "5ba11ced6937c9058565036b": {
    //     "memberType": "user",
    //     "groupsIds": [
    //       "5ba41c98974107060e97da03",
    //       "5bae8505db80230657b2a3ad"
    //     ],
    //     "groupType": "user_group"
    //   },
    //   // ...
    // }

    const memberToGroup = {};

    indirectBindings.forEach((indirectBinding) => {
      const {
        groupIds,
        groupType,
        memberId,
        memberType,
      } = indirectBinding;

      if (!memberToGroup[memberId]) {
        memberToGroup[memberId] = { memberType, groupIds: [], groupType };
      }

      groupIds.forEach((groupId) => {
        if (memberToGroup[memberId].groupIds.indexOf(groupId) === -1) {
          memberToGroup[memberId].groupIds.push(groupId);
        }
      });
    });

    // Then create an array of payloads of members to remove from groups which will
    // be passed to DirectoryGroupingModule/updateMember dispatches to sever the
    // member's group membership, e.g.:
    //
    // [
    //   {
    //     groupId: "5ba41c98974107060e97da03"
    //     groupType: "user_group"
    //     operation: "remove",
    //     memberId: "5ba11ced6937c9058565036b",
    //     memberType: "user",
    //   },
    //   {
    //     groupId: "5ba41c98974107060e97da03"
    //     groupType: "user_group"
    //     operation: "remove",
    //     memberId: "5ba11ced6937c9058565036b",
    //     memberType: "user",
    //   },
    //   // ...
    // ]

    const payloads = [];

    Object.keys(memberToGroup).forEach((memberId) => {
      const { memberType, groupType } = memberToGroup[memberId];

      memberToGroup[memberId].groupIds.forEach((groupId) => {
        payloads.push({
          groupId,
          groupType,
          operation: DirectoryGroupingConsts.operations.remove,
          memberId,
          memberType,
        });
      });
    });

    const groupIds = payloads.map(payload => payload.groupId);
    const itemIds = indirectBindings.map(indirectBinding => indirectBinding.itemId);

    return dispatch('processRemoveIndirectBindings', {
      payloads,
      groupIds,
      itemIds,
      unselectUserGroup,
    });
  }

  resetDeletedItemIds({ commit }, key) {
    commit('RESET_DELETED_ITEM_IDS', key);
  }

  /**
   * Remove one or more users from one or more user groups that indirectly
   * bound the user(s) to one or more systems. To do this, the following series
   * of actions is taken:
   *
   * 1) For each payload, dispatch the updateMember action to the
   *    DirectoryGroupingModule store to remove the user from the user group.
   * 2) Then for each group, dispatch unselectItem to UserGroupCollection to remove
   *    each user group from the list of selected user groups.
   * 3) Then, for each item:
   *    - Remove the paths to the groups that the member was removed from
   *    - Unselect the item
   *    - If the item was not previously directly bound then also remove
   *      the item from the previously selected items.
   * 4) Finally, if the bound items have been fetched then dispatch an action
   *    to refresh the bound items.
   *
   * @param {Object[]} payloads - Array of payload objects
   * @param {string} payloads[].groupId
   * @param {string} payloads[].groupType
   * @param {string} payloads[].operation
   * @param {string} payloads[].memberId
   * @param {string} payloads[].memberType
   * @param {string[]} groupIds - Array of group IDs the member is removed from
   * @param {string[]} itemIds - Array of indirectly bound item IDs
   * @param {boolean} unselectUserGroup - Unselect the user group after removing the member if true
   */
  processRemoveIndirectBindings(context = {}, {
    payloads,
    groupIds,
    itemIds,
    unselectUserGroup,
  }) {
    return new Promise((resolve, reject) => {
      const {
        commit,
        dispatch,
        getters,
        state,
      } = context;

      // For each payload, dispatch updateMember to DirectoryGroupingModule to
      // remove the member from the group. This will remove the indirect
      // binding from the item to the member.

      const tasks = payloads.map(payload => cb => dispatch('DirectoryGroupingModule/updateMember', payload, { root: true })
        .then(cb)
        .catch(error => cb(error)));

      parallelLimit(tasks, 5, (error) => {
        if (error) {
          reject(error);
        } else {
          // For each group, dispatch unselectItem to UserGroupCollection to remove
          // each user group from the list of selected user groups and then
          // dispatch the removePreviouslySelectedItem action so that the user
          // isn't removed from the user groups _again_, when the save button
          // is clicked, which would cause a 404 error because the user was
          // just removed above.
          //
          // Note that this is _only_ done when the unselectUserGroup parameter
          // is true which is set by the ItemTableIndirectBindingMixin in the
          // User Aside Panel. In this case the user is being removed from a
          // user group and the user groups should be updated to reflect that.
          // In other panels this is not necessary.

          if (unselectUserGroup) {
            groupIds.forEach((groupId) => {
              dispatch('UserGroupCollection/unselectItem', { id: groupId }, { root: true });
              dispatch('UserGroupCollection/removePreviouslySelectedItem', { id: groupId }, { root: true });
            });
          }

          // Finally, for each item:
          //
          // - Remove the paths to the groups that the member was removed from
          // - And if there are no paths from the item to the parent then:
          //   - Unselect the item
          //   - If the item was not previously directly bound then also remove
          //     the item from the previously selected items.
          // - If there are paths from the item to the parent then do not
          //   unselect the item since it is indirectly bound to the parent by
          //   a group membership that still persists.

          itemIds.forEach((itemId) => {
            groupIds.forEach(groupId => commit('REMOVE_ITEM_PATHS', { id: itemId, groupId }));

            const item = state.entities.items.byId[itemId];

            if (item && item.paths && item.paths.length === 0) {
              dispatch('unselectItem', { id: itemId });

              if (!getters.isItemPreviouslyDirectlyBound(itemId)) {
                commit('REMOVE_PREVIOUSLY_SELECTED_ITEM', { id: itemId });
              }
            }
          });

          if (state.features.haveBoundItemsBeenFetched) {
            // When bound items have been fetched then we have to refresh the
            // set of bound items because they will have changed after removing
            // the member from the group.

            dispatch('refreshBoundItems')
              .then(() => resolve())
              .catch(error2 => reject(error2));
          } else {
            resolve();
          }
        }
      });
    });
  }

  removePreviouslySelectedItem({ commit }, { id }) {
    commit('REMOVE_PREVIOUSLY_SELECTED_ITEM', { id });
  }

  /**
   * Make a call to the bound items URL endpoint, determine the bound items
   * that have been removed, added and already exist; and then commit those
   * changes to the store.
   *
   * This method is called when showing bound items after removing a member
   * from a group. The processRemoveIndirectBindings() method will dispatch
   * this action to retrieve a fresh copy of the bound items that reflects the
   * group membership that was just removed.
   */
  refreshBoundItems(context = {}) {
    return this.boundItemsApi
      .initialize(context.state.features.boundItemsUrl, this.Api.defaultParams)
      .then((newBoundItems) => {
        const { boundItems } = context.state.entities;
        const { previouslyDirectlyBoundItems } = context.state.features;

        // Remove any pending direct binding paths from the set of bound items
        // just returned from the server. And then remove any refreshed bound
        // items that have no paths. This is necessary because an item that was
        // directly bound, but has been unselected in the UI, will still have
        // the direct binding present on the server because they are not removed
        // immediately, but rather when the admin user clicks the save button.

        newBoundItems.results.forEach((newBoundItem) => {
          const { id } = newBoundItem;
          const directBindingPath = previouslyDirectlyBoundItems.byId[id];

          if (directBindingPath) {
            newBoundItem.paths = newBoundItem.paths
              .filter(path => isEqual(path, directBindingPath) === false);
          }
        });

        newBoundItems.results = newBoundItems.results
          .filter(newBoundItem => newBoundItem.paths.length > 0);

        // Now with the pending direct bindings removed, and any bound items
        // that have no paths to the parent filtered, determine the changes to
        // sync to the client side set of bound items from the server.

        const boundItemIds = Object.keys(boundItems.byId);
        const newBoundItemIds = newBoundItems.results.map(newBoundItem => newBoundItem.id);

        const removedBoundItemIds = boundItemIds.reduce((acc, id) => {
          if (newBoundItemIds.indexOf(id) === -1) {
            acc.push(id);
          }

          return acc;
        }, []);

        const addedBoundItemIds = newBoundItemIds.reduce((acc, id) => {
          if (boundItemIds.indexOf(id) === -1) {
            acc.push(id);
          }

          return acc;
        }, []);

        const existingBoundItemIds = newBoundItemIds.reduce((acc, id) => {
          if (boundItemIds.indexOf(id) !== -1) {
            acc.push(id);
          }

          return acc;
        }, []);

        context.commit('REFRESH_BOUND_ITEMS', {
          newBoundItems: newBoundItems.results,
          removedBoundItemIds,
          addedBoundItemIds,
          existingBoundItemIds,
        });
      });
  }

  refreshCollection(
    context = {},
    {
      item,
      skip = context.state.features.items.pagination.skip || '',
      forceRefresh = false,
    } = {},
  ) {
    const { entities, features } = context.state;
    const { limit } = features.items.pagination;
    const { sort } = features.items;
    const { searchTerm } = features.items;
    const filters = context.getters.getFilters;

    // If passed an item then this means the User Aside Panel just created or
    // updated one and we try to sync it in our items collection immediately,
    // so changes can be reflected in the UI regardless of whether we proceed
    // with refreshing the collection below.

    if (item) {
      // In the SystemUserModel store the item does not have an id property but
      // the CollectionModule derived stores do, so an id property is added
      // here.
      context.dispatch('syncItem', {
        id: item._id,
        attributes: { ...item, ...{ id: item._id } },
      });
    }

    if (!context.getters.canRefreshCollection && !forceRefresh) {
      return Promise.resolve();
    }

    return this
      .fetchWrapper(context, skip, limit, sort, searchTerm, filters)
      .then((items) => {
        const payload = {
          skip,
          limit,
          results: items.results,
          totalCount: items.totalCount,
        };

        if (entities.items.totalCountOfAllItems !== items.totalCount) {
          payload.totalCountOfAllItems = items.totalCount;
        }

        context.commit('REFRESH_COLLECTION', payload);

        // After refreshing the collection if new items were added then set the
        // pagination skip parameter so duplicate items aren't fetched when
        // scrolling to the bottom of the table of new items. Note that skip is
        // set to the beginning of the previous batch of items!

        if (features.items.ids.length > features.items.pagination.skip) {
          // But don't set skip to a value less than 0! This could happen when
          // the count of total items is smaller than the default limit. e.g.
          // 8 total items, limit is 50. Setting the skip parameter to a negative
          // value will cause later API requests to fail and future collectiom
          // refreshes won't work.

          let skip2 = features.items.ids.length - limit;
          skip2 = skip2 < 0 ? 0 : skip2;

          context.commit('SET_PAGINATION_SKIP', { skip: skip2 });
        }

        // And finally, refresh the bound items total count when not viewing
        // bound items, after refreshing the collection to avoid a potential
        // race condition if the boundItemsTotalCount is decremented by
        // REFRESH_COLLECTION and SET_BOUND_ITEMS_TOTAL_COUNT simultaneously.

        if (features.boundItemsUrl) {
          this.Api
            .getBoundItemCount(features.boundItemsUrl)
            .then((totalCount) => {
              context.commit('SET_BOUND_ITEMS_TOTAL_COUNT', { totalCount });
            });
        }
      });
  }

  resetCollection({ commit }, { soft = false, preserveTotalCount = undefined } = {}) {
    commit('RESET_COLLECTION', { soft, preserveTotalCount });
  }

  restorePreviousDirectBinding({ commit }, id) {
    commit('RESTORE_PREVIOUS_DIRECT_BINDING', id);
  }

  selectAllItems(context, { setCollection = true } = {}) {
    return new Promise((resolve, reject) => {
      const { entities, features } = context.state;
      const { searchTerm } = features.items;
      const { filters } = features.items;

      // When showing bound items and the bound items have already been
      // retrieved then fetch a fresh copy of the bound items from the server
      // so that the original set of bound items is available so that any that
      // were previously unselected can be reselected.

      const fetchAllBoundItems = () => this.boundItemsApi
        .initialize(features.boundItemsUrl, this.Api.defaultParams)
        .then((items) => {
          // Create a byId hash from the original bound items, the same as how
          // items are normalized in the store, so that the original bound items
          // can be passed to the fetchAll() method and the search parameter used
          // to filter them.

          const byId = items.results.reduce((accumulator, result) => {
            syncIdField(result, this.idField);
            accumulator[result.id] = result;
            return accumulator;
          }, {});

          return this.boundItemsApi.fetchAll(searchTerm, byId);
        });

      const fetchAllItems = () => this.Api.fetchAll(searchTerm, null, filters);

      const processAllItems = (items) => {
        // since all items were just fetched, put them into the collection
        if (setCollection) { context.commit('SET_COLLECTION', { results: items }); }

        // Filter out items that aren't selectable
        const selectableItems = items.filter(context.getters.isItemSelectable);

        const ids = selectableItems.map((item) => {
          syncIdField(item, this.idField);
          return item.id;
        });

        context.commit('SELECT_ITEMS', { ids });

        // Add all items that were selected to the boundItems byId object
        // in the store so that when toggling to the show bound items view
        // data for the newly selected items is displayed in the item
        // table.

        context.commit('ADD_BOUND_ITEMS', selectableItems);

        if (!features.showBoundItems
            && features.boundItemsUrl
            && !features.haveBoundItemsBeenFetched) {
          // When not showing bound items the IDs of all selected items
          // need to be checked to see if they are bound items. If any of
          // them are then add them to the previouslySelectedItems ID array.

          this
            .initializeBoundItems(context, false)
            .then(() => {
              context.commit('SET_BOUND_ITEMS_TOTAL_COUNT', {
                totalCount: Object.keys(entities.boundItems.byId).length,
              });
              resolve();
            });
        } else if (features.showBoundItems) {
          // When showing bound items new items may have been selected prior to
          // navigating to the show bound items view and then de-selected in the
          // show bound items view. Those item's won't be found when the initial
          // set of bound items is retrieved. So to address this edge case and
          // ensure that the unselected, newly bound, items are re-selected; we
          // just ensure that all items that are currently visible in the table
          // are selected!

          const availableUnselectedIds = difference(
            features.items.ids,
            features.selectedItems.ids,
          );

          availableUnselectedIds
            .forEach(id => context.dispatch('selectItem', { id }));

          resolve();
        } else {
          resolve();
        }
      };

      let functionToCall = fetchAllItems;

      if (features.showBoundItems
          && features.boundItemsUrl
          && features.haveBoundItemsBeenFetched) {
        functionToCall = fetchAllBoundItems;
      }

      functionToCall()
        .then(processAllItems)
        .catch((error) => {
          reject(error);
        });
    });
  }

  selectItem({ commit, getters, state }, { id }) {
    if (getters.isItemPreviouslyDirectlyBound(id)) {
      commit('RESTORE_PREVIOUS_DIRECT_BINDING', id);
    }

    commit('SELECT_ITEM', { id });
    commit('ADD_BOUND_ITEM', state.entities.items.byId[id]);
  }

  setBoundItemsUrl({ commit }, { url }) {
    commit('SET_BOUND_ITEMS_URL', { url });
  }

  setDeletedItemIds({ commit }, { deletedItemIds, failedTasks }) {
    commit('SET_DELETED_ITEM_IDS', deletedItemIds);
    commit('SET_UNDELETED_ITEM_ID', failedTasks);
  }

  setPage({ commit }, { pageNumber, limit }) {
    const skip = (pageNumber * limit);
    commit('SET_PAGINATION_LIMIT', { limit });
    commit('SET_PAGINATION_SKIP', { skip });
  }

  setShowBoundItems({ commit }, value) {
    if (value) {
      commit('SHOW_BOUND_ITEMS');
    } else {
      commit('SHOW_ALL_ITEMS');
    }
  }

  setFilter(context, { filter, pagination }) {
    context.commit('RESET_COLLECTION', { soft: true });
    context.commit('SET_FILTER', { filter, pagination });
  }

  setFilters(context, { filtersArray }) {
    context.commit('RESET_COLLECTION', { soft: true });
    filtersArray.forEach((filter) => {
      context.commit('SET_FILTER', { filter });
    });
  }

  setSearchTerm(context, { searchTerm, preventReset }) {
    if (!preventReset) {
      context.commit('RESET_COLLECTION', { soft: true });
    }
    context.commit('SET_SEARCH_TERM', { searchTerm });
  }

  /**
   * Set the sort field. If already sorting on the specified field the sort
   * direction will be reversed. If not already sorting on the specified field
   * then the sort field will be set. When sorting a soft reset is performed so
   * that any selected or modified items aren't lost.
   */
  setSortField(context, { sortField }) {
    const { sort } = context.state.features.items;

    if (sortField === sort) {
      // Reverse sort direction
      sortField = sortField.split(' ').map((v) => {
        if (v.charAt(0) === '-') {
          return v.slice(1);
        }
        return `-${v}`;
      }).join(' ');
    }

    context.commit('RESET_COLLECTION', { soft: true });
    context.commit('SET_SORT_FIELD', { sortField });
  }

  showAllItems(context) {
    context.commit('RESET_COLLECTION', { soft: true, preserveTotalCount: true });
    context.commit('SHOW_ALL_ITEMS');
    return context.dispatch('fetchCollection');
  }

  showBoundItems(context) {
    context.commit('RESET_COLLECTION', { soft: true, preserveTotalCount: true });
    context.commit('SHOW_BOUND_ITEMS');
    return context.dispatch('fetchCollection');
  }

  /**
   * This method syncs an item; can be used to sync items from one store to another
   */
  syncItem(context, { id, attributes }) {
    context.commit('UPDATE_ITEM', { id, attributes });
  }

  unselectAllItems(context) {
    return new Promise((resolve, reject) => {
      const { entities, features } = context.state;
      const { searchTerm } = features.items;
      const { filters } = features.items;

      this.getApi(features.showBoundItems)
        .fetchAll(searchTerm, entities.boundItems.byId, filters)
        .then((items) => {
          const ids = items.map((item) => {
            syncIdField(item, this.idField);
            return item.id;
          });

          ids.forEach(id => context.dispatch('unselectItem', { id }));

          // When not showing bound items the IDs of the selected items
          // need to be checked to see if they are bound items. If any of
          // them are then add them to the previouslySelectedItems ID array.

          if (!features.showBoundItems
              && features.boundItemsUrl
              && !features.haveBoundItemsBeenFetched) {
            this.initializeBoundItems(context, false)
              .then(() => {
                context.commit('SET_BOUND_ITEMS_TOTAL_COUNT', {
                  totalCount: Object.keys(entities.boundItems.byId).length,
                });

                resolve();
              })
              .catch((error) => {
                reject(error);
              });
          } else {
            resolve();
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  unselectItem({ commit, getters }, { id }) {
    if (getters.isItemDirectlyBound(id) && getters.isItemIndirectlyBound(id, false)) {
      commit('REMOVE_ITEM_DIRECT_BINDING', { id });
    } else {
      commit('UNSELECT_ITEM', { id });
      commit('REMOVE_BOUND_ITEM', { id });
    }
  }

  unselectItems({ commit }, { ids }) {
    commit('UNSELECT_ITEMS', { ids });
    commit('REMOVE_BOUND_ITEMS', { ids });
  }

  /**
   * This method updates an item with `id` to have the provided arguments
   */
  updateItem(context, { id, attributes }) {
    /**
     * When updating an item in an ItemTable the item to update is present in
     * the boundItems collection. However, when updating an item in an
     * AsidePanel the item to update is present in the items collection. To
     * account for that difference in a backwards compatible manner, the
     * item is first referenced in the boundItems records, and if not found
     * then then it is references from the items records.
     */

    const existingItem = context.state.entities.boundItems.byId[id]
      || context.state.entities.items.byId[id];

    const item = merge({}, existingItem, attributes);

    return this.Api.update(item)
      .then(() => {
        context.commit('UPDATE_ITEM', { id, attributes });
      });
  }

  /**
   * This method batch updates all items to have the provided arguments.
   * @param ids Array of the IDs of items to update.
   * @param attributes If an object then set these attributes on all the items
   * with the matching IDs. If an array then set the attributes from each
   * record in the attributes array on the corresponding item by index.
   */
  updateItems({ dispatch }, { ids, attributes }) {
    return new Promise((resolve) => {
      const response = { numSuccess: 0 };
      const tasks = ids.map((id, index) => {
        const updateTask = (cb) => {
          dispatch('updateItem', {
            id,
            attributes: Array.isArray(attributes) ? attributes[index] : attributes,
          })
            .then((result) => {
              if (result instanceof Error) {
                throw result;
              }

              response.numSuccess += 1;
              dispatch('ProgressModule/incrementCompleted', {}, { root: true });
              cb();
            })
            .catch(() => {
              dispatch('ProgressModule/incrementCompleted', {}, { root: true });
              cb();
            });
        };
        return updateTask;
      });

      if (tasks.length) {
        dispatch('ProgressModule/updateBulkActionStatus', true, { root: true });
        dispatch('ProgressModule/setCounter', {
          completed: 0,
          total: tasks.length,
        }, { root: true });
        parallelLimit(tasks, 5, () => {
          dispatch('ProgressModule/setCounter', {
            completed: tasks.length,
            total: tasks.length,
          }, { root: true });

          dispatch('ProgressModule/updateBulkActionStatus', false, { root: true });
          resolve(response);
        });
      } else {
        resolve();
      }
    });
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  /**
   * All items are selected if:
   *
   * 1) When searching all item ids are in the array of selected item ids.
   * 2) When viewing bound items and the bound items total count is equal to
   *    the items total count.
   * 3) When the count of bound items has been set and the count of bound items
   *    equals the total count of all items.
   * 4) Otherwise when the selected items count is equal to the total
   *    items count and neither are equal to zero.
   *
   * @todo Are there other edge cases to consider?
   */
  areAllItemsSelected(state = {
    entities: { items: { totalCountOfAllItems: undefined } },
    features: { items: { searchTerm: undefined }, selectedItems: { ids: [] } },
  }, getters = {}) {
    let allItemsSelected = false;
    const { entities, features } = state;
    const appliedFilters = getters.getAppliedFilters;

    if ((features.items && features.items.searchTerm) || appliedFilters.length) {
      // Assume all items are selected
      allItemsSelected = true;

      // Iterate through the current set of item IDs and ensure that each id is
      // present in the selected items id array.
      features.items.ids.forEach((itemId) => {
        if (features.selectedItems.ids.indexOf(itemId) === -1) {
          allItemsSelected = false;
        }
      });
    } else if (features.showBoundItems
               && entities.boundItems.totalCount === entities.items.totalCount
               && entities.boundItems.totalCount !== 0
               && entities.items.totalCount !== 0) {
      allItemsSelected = true;
    } else if (features.hasBoundItemsTotalCountBeenSet
               && entities.boundItems.totalCount === entities.items.totalCountOfAllItems) {
      allItemsSelected = true;
    } else if (entities.items.totalCountOfAllItems === features.selectedItems.ids.length
               && entities.items.totalCountOfAllItems !== 0
               && features.selectedItems.ids.length !== 0) {
      allItemsSelected = true;
    }

    return allItemsSelected;
  }

  areOnlySomeItemsSelected(state, getters) {
    const { getSelectedItemsCount, areAllItemsSelected } = getters;
    return getSelectedItemsCount > 0 && !areAllItemsSelected;
  }

  areShowingBoundItems(state = { features: {} }) {
    return state.features.showBoundItems;
  }

  /**
   * Don't refresh the collection when:
   *
   * 1) Viewing bound items
   * 2) Searching
   * 3) Filtering
   * 4) The selection has changed (i.e. new items have been selected or
   *    existing items have been unselected).
   *
   * This is necessary because refreshing the collection in these cases can
   * potentially modify the count of selected items to display a value that
   * will be confusing to the admin user.
   */
  canRefreshCollection(state = { features: { items: {} } }, getters = {}) {
    const { features } = state;
    const { boundItemsUrl, showBoundItems } = features;
    const { searchTerm } = features.items;
    const { getAppliedFilters = [], getItemsToBind = [], getItemsToUnbind = {} } = getters;
    const hasSelectionChanged = getItemsToBind.length > 0 || getItemsToUnbind.length > 0;

    return !((boundItemsUrl && showBoundItems)
      || searchTerm
      || getAppliedFilters.length
      || hasSelectionChanged);
  }

  getBoundItem(state = { entities: { boundItems: { byId: {} } } }) {
    return id => state.entities.boundItems.byId[id];
  }

  getBoundItemsTotalCount(state = { entities: { boundItems: { totalCount: 0 } } }) {
    return state.entities.boundItems.totalCount;
  }

  getCollection(state = { features: { items: { ids: [] } } }) {
    return state.features.items.ids.reduce((accumulator, id) => {
      accumulator.push(state.entities.items.byId[id]);
      return accumulator;
    }, []);
  }

  /**
   * @todo Refactor this to work with any item type! Right now it's specific to
   * system users but present on the base CollectionModule. It shouldn't be hard
   * to determine the proper display name for all types of items.
   */
  getDisplayName(state, getters = {}) {
    return (item) => {
      const name = (getters.getFirstAndLastName && getters.getFirstAndLastName(item));
      if (name) {
        return name;
      }
      if (item.username) {
        return item.username.trim();
      }
      return '';
    };
  }

  getAppliedFilters(state = { features: { items: { filters: [] } } }) {
    const { filters } = state.features.items;
    return filters.filter(filter => filter.value !== '');
  }

  getFilters(state = { features: { items: { filters: [] } } }) {
    const { filters } = state.features.items;
    return filters;
  }

  getFirstAndLastName() {
    return (item) => {
      const firstName = item.firstname;
      const lastName = item.lastname;

      if (firstName && lastName) {
        return `${firstName} ${lastName}`.trim();
      }

      if (firstName && !lastName) {
        return firstName.trim();
      }

      if (!firstName && lastName) {
        return lastName.trim();
      }

      return '';
    };
  }

  getItem(state = { entities: { items: { byId: {} } } }) {
    return id => state.entities.items.byId[id];
  }

  getItemPaths(state = { entities: { boundItems: [], items: { byId: {} } } }) {
    return (id) => {
      const { boundItems, items } = state.entities;

      if (items.byId[id] && items.byId[id].paths) {
        return items.byId[id].paths;
      }
      if (boundItems.byId[id] && boundItems.byId[id].paths) {
        return boundItems.byId[id].paths;
      }

      return undefined;
    };
  }

  /**
   * Return array of objects to bind.
   *
   * If an item in the selectedItems ID array array is in the
   * previouslySelectedItems ID array then don't return it here since it
   * was previously bound and attempting to bind it again will result in a
   * 5xx error. In this use case, the admin user likely selected,
   * unselected, and then selected it again.
   */
  getItemsToBind(state = { features: { selectedItems: { ids: [] } } }) {
    return state.features.selectedItems.ids.reduce((itemsToBind, id) => {
      if (state.features.previouslySelectedItems.ids.indexOf(id) === -1) {
        itemsToBind.push({ id });
      }

      return itemsToBind;
    }, []);
  }

  /**
   * Return array of items to unbind.
   *
   * If an item in the unselectedItems.ids array is not in the
   * previouslySelectedItems ID array then don't return it here since it
   * was not previously bound and attempting to unbind it will result in
   * a 5xx  error. In this use case, the admin user either:
   *
   * 1) Selected and then unselected the item
   * 2) The item was initially selected, then unselected, and then re-selected
   */
  getItemsToUnbind(state = {
    features: {
      unselectedItems: { ids: [] },
      previouslyDirectlyBoundItems: { byId: {} },
    },
  }) {
    const { features } = state;
    const itemIdsToUnbind = [];

    features.unselectedItems.ids.forEach((id) => {
      if (features.previouslySelectedItems.ids.indexOf(id) !== -1) {
        itemIdsToUnbind.push(id);
      }
    });

    Object.keys(features.previouslyDirectlyBoundItems.byId).forEach((id) => {
      if (itemIdsToUnbind.indexOf(id) === -1) {
        itemIdsToUnbind.push(id);
      }
    });

    // Return an array of objects with an id property for backwards compatability
    // with existing getUnselected() method in ItemList.
    return itemIdsToUnbind.map(id => ({ id }));
  }

  getMaxNumBoundItems(state = { features: {} }) {
    return state.features.maxNumBoundItems;
  }

  getSkipToken(state = { features: { items: { pagination: {} } } }) {
    return get(state, 'features.items.pagination.skipToken', null);
  }

  getNextPageToken(state = { features: { items: { pagination: {} } } }) {
    return get(state, 'features.items.pagination.nextPageToken', null);
  }

  getPage(state = { features: { items: { pagination: {} } } }) {
    const skip = get(state, 'features.items.pagination.skip', 0);
    const limit = get(state, 'features.items.pagination.limit', 0);
    if (skip > 0) {
      return skip / limit;
    }
    return 0;
  }

  getRowsPerPage(state = { features: { items: { pagination: {} } } }) {
    return get(state, 'features.items.pagination.limit', 0);
  }

  /**
   * Given the paths from a target item to its parent, return the parent type,
   * parent group type, the group IDs that the parent is a member of, and the
   * target item type. This is used to inform admin users of the membership
   * details that bind the parent to the target.
   * @todo Is this method still in use?
   */
  getPathInfoForParent(state, getters = {}) {
    return (id) => {
      let targetItemType;
      let parentGroupType;
      let parentType;
      const parentGroupIds = [];
      const paths = (getters.getItemPaths && getters.getItemPaths(id)) || [];

      // Only include paths for indirect bindings which have a length greater
      // than one. A direct binding will have a path from the target to the
      // parent with a length of 1.

      paths
        .filter(path => path.length > 1)
        .forEach((path) => {
          targetItemType = path[path.length - 1].to.type;
          parentGroupType = path[0].to.type;
          [parentType] = parentGroupType.split('_');

          // The parent may be a member of a group that is bound to more than one
          // group that the target is a member of. In this case the admin only
          // needs to be informed of the parent group once.

          const parentGroupId = path[0].to.id;

          if (parentGroupIds.indexOf(parentGroupId) === -1) {
            parentGroupIds.push(path[0].to.id);
          }
        });

      return {
        parentType,
        parentGroupType,
        parentGroupIds,
        targetItemType,
      };
    };
  }

  /**
   * Given the paths from a target item to its parent, return the target item
   * type, target group type, target group IDs, and parent type. This is used
   * to inform admin users of how the target is bound to the parent and to ask
   * if they'd like to create a direct binding.
   * @todo Is this method still in use?
   */
  getPathInfoForTarget(state, getters = {}) {
    return (id) => {
      let targetItemType;
      let targetGroupType;
      const targetGroupIds = [];
      let parentType;

      const paths = (getters.getItemPaths && getters.getItemPaths(id)) || undefined;

      // Only include paths for indirect bindings which have a length greater
      // than one. A direct binding will have a path from the target to the
      // parent with a length of 1.

      paths
        .filter(path => path.length > 1)
        .forEach((path) => {
          targetGroupType = path[path.length - 2].to.type;
          [targetItemType] = targetGroupType.split('_');

          // The target may be a member of a group that is bound to more than
          // one group that the parent is a member of. In this case the admin
          // only needs to be informed of this target group once.

          const targetGroupId = path[path.length - 2].to.id;

          if (targetGroupIds.indexOf(targetGroupId) === -1) {
            targetGroupIds.push(targetGroupId);
          }

          [parentType] = path[0].to.type.split('_');
        });

      return {
        targetItemType,
        targetGroupType,
        targetGroupIds,
        parentType,
      };
    };
  }

  getPreviouslyDirectlyBoundItems(
    state = { features: { previouslyDirectlyBoundItems: { byId: {} } } },
  ) {
    return Object.keys(state.features.previouslyDirectlyBoundItems.byId);
  }

  getPreviouslyDirectlyBoundItemsCount(
    state = { features: { previouslyDirectlyBoundItems: { byId: {} } } },
  ) {
    return Object.keys(state.features.previouslyDirectlyBoundItems.byId).length;
  }

  getSearchTerm(state = { features: { items: {} } }) {
    return state.features.items.searchTerm;
  }

  getSearchField() {
    return searchTerm => this.Api.getSearchField(searchTerm);
  }

  getSelectedItems(state = { features: { selectedItems: { ids: [] } } }) {
    return state.features.selectedItems.ids;
  }

  getSelectedItemsCount(state = { features: { selectedItems: { ids: [] } } }) {
    let selectedItemsTotalCount = 0;

    if (state.features.hasBoundItemsTotalCountBeenSet) {
      selectedItemsTotalCount = state.entities.boundItems.totalCount;
    } else {
      selectedItemsTotalCount = state.features.selectedItems.ids.length;
    }

    return selectedItemsTotalCount;
  }

  /**
   * This method returns the selected items that are bound to a parent by the
   * specified groups. It is used when unselecting indirectly bound items so
   * that other items that are indirectly bound by the same group membership
   * that is being removed, can also be unselected.
   * @param groupIds The array of group IDs the member is being removed from
   * @param groupType The type of the groups the member is being removed from
   * @return {string[]} Array of selected item IDs that are indirectly bound to
   * the parent (member) by the specified groups.
   */
  getSelectedItemsIndirectlyBoundByGroup(state, getters = { getSelectedItems: [] }) {
    return (groupIds, groupType) => getters.getSelectedItems.reduce((acc, id) => {
      let groupMembership;

      // The selected item _might_ not be in the collection of items, in
      // which case getGroupMembership would fail. That edge case occurs when
      // items have been selected and a search term has been entered. To avoid
      // that edge case the groupMembership is not retrieved if thte item is
      // not present.
      if (getters.getItem(id)) {
        groupMembership = getters.getGroupMembership(id, groupType);
      }

      groupIds.forEach((groupId) => {
        if (groupMembership
              && groupMembership.indexOf(groupId) !== -1
              && acc.indexOf(id) === -1) {
          acc.push(id);
        }
      });

      return acc;
    }, []);
  }

  getSortField(state = { features: { items: { sort: '' } } }) {
    return state.features.items.sort;
  }

  getTotalCount(state = { entities: { items: { totalCount: 0 } } }) {
    return state.entities.items.totalCount;
  }

  getTotalCountOfAllItems(state = { entities: { items: { totalCountOfAllItems: undefined } } }) {
    return state.entities.items.totalCountOfAllItems;
  }

  /**
   * In general the total count of _active_ items is the same as the total
   * count of all items. However, this method will be overridden by the
   * DirectoryCollection because inactive directories are currently returned in the
   * results from GET /api/v2/directories but the Getting Started Panel should
   * be shown in the Main Panel when there are no _active_ directories.
   */
  getTotalCountOfActiveItems(state = { entities: { items: { totalCountOfAllItems: undefined } } }) {
    return state.entities.items.totalCountOfAllItems;
  }

  getGroupMembership(state, getters) {
    // Currently two options exist for groupType in DirectoryGroupingConsts:
    // groupTypes.userGroup and groupTypes.systemGroup
    return (id, groupType) => {
      const groupIds = [];
      const paths = (getters.getItemPaths && getters.getItemPaths(id)) || undefined;

      if (paths) {
        paths
          .filter(path => path.length > 1)
          .forEach((path) => {
            const group = find(path, node => node.to.type === groupType);

            if (group) {
              // The parent may be a member of different groups that are bound to the same
              // group that the item is a member of. In this case the admin user only needs
              // to be informed of the item's group membership once.

              const groupId = group.to.id;

              if (groupIds.indexOf(groupId) === -1) {
                groupIds.push(groupId);
              }
            }
          });
      }

      return groupIds;
    };
  }

  isItemDirectlyBound(state = { entities: { items: {}, boundItems: {} } }) {
    return (id) => {
      const { items, boundItems } = state.entities;
      let paths;

      // The item may no longer be in the items if the search or sort parameters
      // have changed. But in those cases, if it was selected then it should be
      // in the boundItems; so both collections are checked!

      if (items.byId[id]) {
        ({ paths } = items.byId[id]);
      } else if (boundItems.byId[id]) {
        ({ paths } = boundItems.byId[id]);
      }

      let directlyBound = false;

      if (paths && paths.length) {
        paths.forEach((path) => {
          if (path.length === 1) {
            directlyBound = true;
          }
        });
      }

      return directlyBound;
    };
  }

  isItemIndirectlyBound(state = { entities: { items: {}, boundItems: {} } }, getters) {
    return (id, checkForDirectBinding = true) => {
      const { items, boundItems } = state.entities;
      let paths;

      // The item may no longer be in the items if the search or sort parameters
      // have changed. But in those cases, if it was selected then it should be
      // in the boundItems; so both collections are checked!
      //
      // Note that the paths may also not be present in the items collection
      // due to the the interaction of the filter and skip/limit parameters in
      // the bound items URL. For example, when searching, items that match the
      // search criteria may not fall into the result set determined by the
      // skip/limit parameters so they won't be returned when filtering by id.
      // Filtering by id _only_ works when the ids to match are found in
      // the result set determined by the skip/limit params.

      if (items.byId[id]) {
        ({ paths } = items.byId[id]);
      }

      if (!paths && boundItems.byId[id]) {
        ({ paths } = boundItems.byId[id]);
      }

      let indirectlyBound = false;

      if (paths && paths.length) {
        paths.forEach((path) => {
          if (path.length > 1) {
            indirectlyBound = true;
          }
        });
      }

      // Allow the caller to check for an indirect binding alone. This is used
      // in unselectItem() so that a direct and indirect binding can be checked
      // for independently, so that the direct binding can be removed first.
      //
      // Most of the time when checking to see if an item is indirectly bound
      // we let a direct binding take higher priority over an indirect binding.
      // This is done so that in the ItemTable, if an item is both directly and
      // indirectly bound to a parent object, the checkbox can be displayed
      // with full opacity. However, in the case discussed above, in the
      // unselectItem() method, both types of bindings need to be checked for
      // independently.

      if (checkForDirectBinding) {
        return indirectlyBound && !getters.isItemDirectlyBound(id);
      }

      return indirectlyBound;
    };
  }

  isItemPreviouslyDirectlyBound(state) {
    return id => !!(state.features.previouslyDirectlyBoundItems.byId[id]);
  }

  isItemPreviouslySelected(state = { features: { previouslySelectedItems: { ids: [] } } }) {
    return id => state.features.previouslySelectedItems.ids.includes(id);
  }

  isItemSelectable() {
    return () => true;
  }

  isItemSelected(state = { features: { selectedItems: { ids: [] } } }) {
    return id => state.features.selectedItems.ids.indexOf(id) !== -1;
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  ADD_BOUND_ITEM(state = { entities: { boundItems: [] } }, item = {}) {
    addBoundItem(state.entities.boundItems, item);
  }

  ADD_BOUND_ITEMS(state, items) {
    items.forEach((item) => {
      addBoundItem(state.entities.boundItems, item);
    });
  }

  BOUND_ITEMS_HAVE_BEEN_FETCHED(state) {
    state.features.haveBoundItemsBeenFetched = true;
  }

  BOUND_ITEMS_TOTAL_COUNT_HAS_BEEN_SET(state) {
    state.features.hasBoundItemsTotalCountBeenSet = true;
  }

  DELETE_ALL_FILTERS(state) {
    state.features.items.filters = [];
  }

  DELETE_FILTER(state, { filterName }) {
    const newFilters = state.features.items.filters.filter(f => f.name !== filterName);
    Vue.set(state.features.items, 'filters', newFilters);
  }

  DELETE_FILTERS(state, { filtersArray }) {
    const newFilters = state.features.items.filters
      .filter((f) => (!filtersArray.some(filter => (
        f.name === filter.name
        && f.operator === filter.operator
        && f.value === filter.value
      ))));

    Vue.set(state.features.items, 'filters', newFilters);
  }

  DELETE_ITEMS(state, ids) {
    ids.forEach((id) => {
      delete state.entities.items.byId[id];
      removeItem(state.features.items.ids, id);
      removeItem(state.features.selectedItems.ids, id);
      state.entities.items.totalCount -= 1;
      state.entities.items.totalCountOfAllItems -= 1;
    });
  }

  /**
   * After receiving a fresh copy of the bound items, to account for any changes
   * that may now be present after removing a member from a group, refresh our
   * copy by:
   *
   * 1) For each removed bound item:
   *    a) Remove existing bound item from state.entities.boundItems.byId
   *    b) Remove existing bound item id from state.features.selectedItems.ids
   *    c) Remove existing bound item id from state.features.previouslySelectedItems.ids
   *
   * 2) For each added bound item:
   *    a) Add new bound item to state.entities.boundItems.byId
   *    b) Add new bound item id to state.features.selectedItems.ids
   *    c) Add new bound item id to state.features.previouslySelectedItems.ids
   *
   * 3) For each existing bound item:
   *    a) Copy the new bound item properties to the existing bound item.
   *    b) Update the paths of the bound item if found in the items hash
   *
   *       This ensures that if the bound item was indirectly bound by two
   *       indirect bindings, and one was just removed, the second will be
   *       preserved.
   */
  REFRESH_BOUND_ITEMS(state, {
    newBoundItems,
    addedBoundItemIds,
    removedBoundItemIds,
    existingBoundItemIds,
  }) {
    const getNewBoundItem = id => newBoundItems.find(i => i.id === id);
    const { boundItems, items } = state.entities;
    const { previouslySelectedItems, selectedItems } = state.features;

    removedBoundItemIds.forEach((id) => {
      removeBoundItem(boundItems, id);
      removeItem(selectedItems.ids, id);
      removeItem(previouslySelectedItems.ids, id);
    });

    addedBoundItemIds.forEach((id) => {
      const newBoundItem = getNewBoundItem(id);
      addBoundItem(boundItems, newBoundItem);
      addItem(selectedItems.ids, id);
      addItem(previouslySelectedItems.ids, id);
    });

    existingBoundItemIds.forEach((id) => {
      const newBoundItem = getNewBoundItem(id);
      addBoundItem(boundItems, newBoundItem);

      if (items.byId[id]) {
        Vue.set(items.byId[id], 'paths', newBoundItem.paths);
      }
    });
  }

  /**
   * Refresh the collection from the specified skip through the specified limit
   * parameter as follows:
   *
   * 1) Update state.entities.items
   *    a) If an item exists in the refreshed results but not in the byId hash
   *       then add the new item to the byId hash. Do not attempt to remove
   *       items from the byId hash here. It's OK to leave removed items in the
   *       byId hash as long as their IDs are removed from the features so that
   *       they're not displayed.
   *    b) If the refreshed total count is different then update it.
   *    c) If the refreshed totalCountOfAllItems is different then update it.
   * 2) Update state.features.items.ids:
   *    a) Call the updateItemIds() method to sync the refreshed item IDs with the
   *       existing item IDs.
   *    b) If all items have been removed then reset the ids array.
   * 3) Update state.features.selectedItems.ids and state.entities.boundItems
   *    if items have been removed.
   */
  REFRESH_COLLECTION(state, {
    skip, limit, results, totalCount, totalCountOfAllItems,
  }) {
    /**
     * Merge a subset of refreshed ids into an existing set of ids. Please
     * refer to the comments in the for loop below to understand the approach
     * used to sync the refreshed ids with the existing ids.
     */
    function updateExistingItemIds(existingItemIds, refreshedItemIds, skip2, limit2) {
      // Short-circuit if the existing and refreshed items lists are equivalent
      if (isEqual(existingItemIds.slice(0).sort(), refreshedItemIds.slice(0).sort())) {
        return refreshedItemIds;
      }

      // Create an array of tuples of id and index so that the processing
      // logic below knows what values to expect at which positions.
      const refreshedItemIdsToProcess = refreshedItemIds.map((refreshedItemId, index) => (
        { id: refreshedItemId, index: skip2 + index }
      ));

      // Clone the existingItemIds array into a new variable for modifications to
      // prevent this function from creating side effects.
      const updatedExistingItemIds = [].concat(existingItemIds);

      // If the refreshed slice of the existingItemIds has more ids than the
      // refreshedItemIds then remove the ids from the existingItemIds that are
      // not present in the refreshedItemIds. This covers the use case where
      // items were removed at the end of the collection.

      const existingItemIdsSlice = existingItemIds.slice(skip2, skip2 + limit2);

      if (existingItemIdsSlice.length > refreshedItemIds.length) {
        const existingItemIdsToRemove = difference(existingItemIdsSlice, refreshedItemIds);

        existingItemIdsToRemove.forEach((existingItemIdToRemove) => {
          const indexOfExistingItemIdToRemove = updatedExistingItemIds
            .indexOf(existingItemIdToRemove);
          updatedExistingItemIds.splice(indexOfExistingItemIdToRemove, 1);
        });
      }

      // Used to flag special case on the first iteration when first refreshed id
      // is found in the rest of the existing ids and the corresponding existing
      // id isn't the same.
      let firstIteration = true;

      for (let i = 0; i < refreshedItemIdsToProcess.length; i += 1) {
        const { id, index } = refreshedItemIdsToProcess[i];

        if (updatedExistingItemIds[index] === id) {
          // NOOP - The existing id at this index and the refreshed id at this
          // index are the same, so nothing has changed.
        } else if (updatedExistingItemIds.slice(skip2).indexOf(id) !== -1) {
          // If the first existing id and the first refreshed id are not the
          // same and the first refreshed id exists later in the existing ids
          // then remove both from their respective arrays and recursively call
          // updateExistingItemIds() with the new arrays, keeping the skip
          // parameter the same. This handles the case where all existing ids in
          // the subset of size skip have been removed.

          if (firstIteration === true) {
            updatedExistingItemIds.splice(index, 1);
            const updatedRefreshedItemIds = [].concat(refreshedItemIds);

            if (index !== 0) {
              updatedRefreshedItemIds.splice(0, 1);
            }

            if (updatedRefreshedItemIds.length) {
              return updateExistingItemIds(updatedExistingItemIds, updatedRefreshedItemIds, skip);
            }

            return updatedExistingItemIds;
          }

          // The existing id was removed since one of the next existing ids has
          // the same value as the refreshed id.

          updatedExistingItemIds.splice(index, 1);
        } else {
          // The refreshed id is new so add it to the list at the specified index.
          updatedExistingItemIds.splice(index, 0, id);
        }

        firstIteration = false;
      }

      return updatedExistingItemIds;
    }

    //
    // 1a) Update state.entities.items.byId
    //

    // Convert results to hash of id to object pairs
    // TODO: This is copied from SET_COLLECTION() below and needs to be moved
    // to a common location.
    const hash = results.reduce((accumulator, result) => {
      accumulator[get(result, this.idField)] = result;

      // Ensure each result has an "id" field if the idField is "_id"

      if (this.idField === MONGO_ID) {
        accumulator[get(result, this.idField)].id = get(result, this.idField);
      }

      return accumulator;
    }, {});

    // Merge hash into existing entities. Note that an existing item is updated
    // with the new object attributes.

    Object.keys(hash).forEach((id) => {
      Vue.set(state.entities.items.byId, id, hash[id]);
    });

    //
    // 1b) Update state.entities.items.totalCount
    //

    state.entities.items.totalCount = totalCount;

    //
    // 1c) Update state.entities.items.totalCountOfAllItems
    //

    if (typeof totalCountOfAllItems === 'number') {
      state.entities.items.totalCountOfAllItems = totalCountOfAllItems;
    }

    //
    // 2) Update state.features.items.ids
    //

    const refreshedItemIds = results.map(result => get(result, this.idField));
    let updatedItemIds = updateExistingItemIds(
      state.features.items.ids,
      refreshedItemIds,
      skip,
      limit,
    );

    if (totalCount === 0 && totalCountOfAllItems === 0) {
      updatedItemIds = [];
    }

    Vue.set(state.features.items, 'ids', updatedItemIds);

    //
    // 3) Ensure removed items are no longer present in the selectedItems,
    //    unselectedItems, and potentially the boundItems too.
    //

    const removedItemIds = Object.keys(state.entities.items.byId).reduce((accumulator, id) => {
      if (state.features.items.ids.indexOf(id) === -1) {
        accumulator.push(id);
      }

      return accumulator;
    }, []);

    removedItemIds.forEach((id) => {
      removeItem(state.features.selectedItems.ids, id);
      removeItem(state.features.unselectedItems.ids, id);

      //
      // Remove the removed item from the bound items too if:
      //
      // 1) Bound items have been fetched and there is a bound items URL
      // 2) Bound items have not been fetched and there is no bound items URL
      //
      // If a bound item is viewed prior to showing the bound items it will not
      // be removed until the admin user clicks on show bound items, then the
      // UI will be updated and the bound item removed.
      //
      // This is done because if we haven't fetched the bound items yet then we
      // won't know if the removed item is a bound item and so we can't
      // accurately decrement the boundItems.totalCount yet.
      //
      // TODO: The above edge case could be resolved by pre-fetching the bound
      // items when a bound items URLs is initially present.
      //

      if ((state.features.haveBoundItemsBeenFetched && state.features.boundItemsUrl)
          || (!state.features.haveBoundItemsBeenFetched && !state.features.boundItemsUrl)) {
        removeBoundItem(state.entities.boundItems, id);
      }
    });
  }

  REMOVE_BOUND_ITEM(state, { id }) {
    removeBoundItem(state.entities.boundItems, id);
  }

  REMOVE_BOUND_ITEMS(state, { ids }) {
    const boundItems = cloneDeep(state.entities.boundItems);

    ids.forEach((id) => {
      removeBoundItem(boundItems, id);
    });

    Vue.set(state.entities, 'boundItems', boundItems);
  }

  REMOVE_ITEM_DIRECT_BINDING(state, { id }) {
    const { entities, features } = state;
    const { paths } = entities.items.byId[id];

    if (!paths) {
      return;
    }

    for (let i = 0; i < paths.length; i += 1) {
      const path = paths[i];

      if (path.length === 1) {
        Vue.set(features.previouslyDirectlyBoundItems.byId, id, path);
        paths.splice(i, 1);
        i -= 1;
      }
    }

    Vue.set(entities.items.byId[id], 'paths', paths);
    Vue.set(entities.boundItems.byId[id], 'paths', paths);
  }

  REMOVE_ITEM_PATHS(state, { id, groupId }) {
    const { paths } = state.entities.items.byId[id];

    for (let i = 0; i < paths.length; i += 1) {
      const path = paths[i];
      const userGroupInPaths = !!(
        path
        && path.length
        && path.length > 0
        && find(path, node => node.to.id === groupId)
      );

      if (userGroupInPaths) {
        paths.splice(i, 1);
        i -= 1;
      }
    }
  }

  REMOVE_PREVIOUSLY_SELECTED_ITEM(state, { id }) {
    removeItem(state.features.previouslySelectedItems.ids, id);
  }

  /**
   * Reset the collection either to the default initial state or preserving
   * certain aspects of the state depending on the payload options.
   * @param soft If true then many properties of the state will not be reset as
   * seen in the if statements below. This is set to true to when setting a
   * search or sort parameter, or when toggling between showing all items and
   * showing only bound items. The default value is false.
   * @param preserveTotalCount If true then then the totalCount and
   * totalCountOfAllItems properties of the entities.items will not be reset.
   * This is set to true when toggling between showing all items and showing
   * only bound items so that the ItemTable component can accurately display
   * these numbers. The default value is false.
   */
  RESET_COLLECTION(state, { soft = false, preserveTotalCount = false } = {}) {
    if (typeof state === 'undefined') return; // For tests where state has not been initialized

    const initialState = this.getInitialState();

    if (preserveTotalCount) {
      Vue.set(state.entities.items, 'byId', initialState.entities.items.byId);
    } else if (soft) {
      Vue.set(state.entities.items, 'byId', initialState.entities.items.byId);
      Vue.set(state.entities.items, 'totalCount', initialState.entities.items.totalCount);
    } else {
      Vue.set(state.entities, 'items', initialState.entities.items);
    }

    if (!soft) {
      Vue.set(state.entities, 'boundItems', initialState.entities.boundItems);
    }

    if (soft) {
      // Preserve the sort order and search term when performing a soft reset.
      Vue.set(state.features.items, 'pagination', initialState.features.items.pagination);
      Vue.set(state.features.items, 'ids', initialState.features.items.ids);
    } else {
      Vue.set(state.features, 'items', initialState.features.items);
    }

    if (!soft) {
      Vue.set(state.features, 'boundItemsUrl', initialState.features.boundItemsUrl);
      Vue.set(state.features, 'haveBoundItemsBeenFetched', initialState.features.haveBoundItemsBeenFetched);
      Vue.set(state.features, 'hasBoundItemsTotalCountBeenSet', initialState.features.hasBoundItemsTotalCountBeenSet);
      Vue.set(state.features, 'previouslyDirectlyBoundItems', initialState.features.previouslyDirectlyBoundItems);
      Vue.set(state.features, 'previouslySelectedItems', initialState.features.previouslySelectedItems);
      Vue.set(state.features, 'selectedItems', initialState.features.selectedItems);
      Vue.set(state.features, 'showBoundItems', initialState.features.showBoundItems);
      Vue.set(state.features, 'unselectedItems', initialState.features.unselectedItems);
    }
  }

  RESET_DELETED_ITEM_IDS(state, key) {
    state.features.deletedItems[key] = [];
  }

  RESTORE_PREVIOUS_DIRECT_BINDING(state, id) {
    const { entities, features } = state;
    const path = features.previouslyDirectlyBoundItems.byId[id];

    if (!entities.items.byId[id].paths) {
      Vue.set(entities.items.byId[id], 'paths', []);
    }

    entities.items.byId[id].paths.push(path);
    entities.boundItems.byId[id].paths.push(path);

    Vue.delete(features.previouslyDirectlyBoundItems.byId, id);
  }

  /**
   * Select an item by adding it to the selectedItems ID array and removing
   * it from the unselectedItems ID array.
   */
  SELECT_ITEM(state, { id }) {
    addItem(state.features.selectedItems.ids, id);
    removeItem(state.features.unselectedItems.ids, id);
  }

  /**
   * Select an array of items by iterating through them for each item
   * adding it to the selectedItems ID array and removing it from the
   * unselectedItems ID array.
   */
  SELECT_ITEMS(state, { ids }) {
    ids.forEach((id) => {
      addItem(state.features.selectedItems.ids, id);
      removeItem(state.features.unselectedItems.ids, id);
    });
  }

  SET_BOUND_ITEMS(state, items) {
    items.results.forEach((result) => {
      result.os = result.os === 'Mac OS X' ? 'macOS' : result.os;
      syncIdField(result, this.idField);
      state.entities.boundItems.byId[result.id] = result;
    });

    state.entities.boundItems.totalCount = items.totalCount;
  }

  SET_BOUND_ITEMS_URL(state, { url }) {
    if (typeof state === 'undefined') return;
    state.features.boundItemsUrl = url;
  }

  SET_BOUND_ITEMS_TOTAL_COUNT(state, { totalCount }) {
    state.entities.boundItems.totalCount = totalCount;
  }

  /**
   * @todo Use the normalizr library to do this for us. For now, it's done
   * manually.
   * @see https://github.com/paularmstrong/normalizr
   */
  SET_COLLECTION(state, items) {
    // Convert results to hash of id to object pairs
    const hash = items.results.reduce((accumulator, result) => {
      accumulator[get(result, this.idField)] = result;

      // Ensure each result has an "id" field if the idField is "_id"

      if (this.idField === MONGO_ID) {
        accumulator[get(result, this.idField)].id = get(result, this.idField);
      }

      return accumulator;
    }, {});

    // Merge hash into existing entities. Note that an existing item is updated
    // with the new object attributes.

    Object.keys(hash).forEach((id) => {
      // Use Vue.set() to ensure that the item is reactive!
      // SEE: https://vuejs.org/v2/guide/reactivity.html#Change-Detection-Caveats
      Vue.set(
        state.entities.items.byId,
        id,
        this.extendExistingObject(hash[id], state.entities.items.byId[id]),
      );
    });

    // Filter out any duplicates: https://jumpcloud.atlassian.net/browse/CA-316
    const ids = items.results.reduce((acc, result) => {
      if (!includes(state.features.items.ids, get(result, this.idField))) {
        acc.push(get(result, this.idField));
      }
      return acc;
    }, []);
    // Finally, concatenate the newly returned IDs to the existing set of IDs.
    state.features.items.ids = state.features.items.ids.concat(ids);

    // Finally update the total count and total count of all items if specified.
    // The difference between the totalCount and totalCountOfAllItems is
    // discussed above in getInitialState().

    if (typeof items.totalCount === 'number') {
      state.entities.items.totalCount = items.totalCount;
    }

    if (typeof items.totalCountOfAllItems === 'number') {
      state.entities.items.totalCountOfAllItems = items.totalCountOfAllItems;
    }

    state.features.items.pagination.skipToken = items.skipToken;

    state.features.items.pagination.nextPageToken = items.nextPageToken;
  }

  SET_DELETED_ITEM_IDS(state, deletedItemIds) {
    Vue.set(state.features.deletedItems, 'deletedItemIds', deletedItemIds);
  }

  SET_ITEM(state, item) {
    if (this.idField === MONGO_ID) {
      item.id = get(item, this.idField);
    }

    Vue.set(
      state.entities.items.byId,
      item.id,
      extend({}, state.entities.items.byId[item.id] || {}, item),
    );
  }

  SET_ITEM_PATHS(state, { id, path }) {
    const item = state.entities.items.byId[id];

    if (!item) {
      return;
    }

    if (!item.paths) {
      Vue.set(item, 'paths', []);
    }

    item.paths.push(path);
  }

  SET_PREVIOUSLY_SELECTED_ITEMS(state, { ids }) {
    ids.forEach((id) => {
      addItem(state.features.previouslySelectedItems.ids, id);
    });
  }

  SET_FILTER(state, { filter, pagination }) {
    const { filters } = state.features.items;

    // If the filter already exists, update the value
    const existingFilterIndex = filters.findIndex(d => (
      (d.name === filter.name && d.operator === filter.operator) || (filter.name === 'osFamily' && d.name === filter.name)
    ));

    if (existingFilterIndex > -1 && filter.duplicate !== true) {
      Vue.set(state.features.items.filters, existingFilterIndex, filter);
      return;
    }

    // If the filter doesn't exist, add the filter
    filters.push(filter);
    Vue.set(state.features.items, 'filters', filters);

    if (pagination) {
      const { limit } = pagination;
      state.features.items.pagination.limit = limit;
    }
  }

  SET_SEARCH_TERM(state, { searchTerm }) {
    state.features.items.searchTerm = searchTerm;
  }

  SET_SORT_FIELD(state, { sortField }) {
    Vue.set(state.features.items, 'sort', sortField);
  }

  SET_PAGINATION_LIMIT(state, { limit }) {
    Vue.set(state.features.items.pagination, 'limit', limit);
  }

  SET_PAGINATION_SKIP(state, { skip }) {
    Vue.set(state.features.items.pagination, 'skip', skip);
  }

  SET_UNDELETED_ITEM_ID(state, failedTasks) {
    Vue.set(state.features.deletedItems, 'failedTasks', failedTasks);
  }

  SHOW_ALL_ITEMS(state) {
    state.features.showBoundItems = false;
  }

  SHOW_BOUND_ITEMS(state) {
    state.features.showBoundItems = true;
  }

  /**
   * Unselect an item by adding to the unselectedItems array and removing
   * if from the selectedItems array.
   */
  UNSELECT_ITEM(state, { id }) {
    addItem(state.features.unselectedItems.ids, id);
    removeItem(state.features.selectedItems.ids, id);
  }

  /**
   * Unselect an array of items by iterating through them amd for each
   * item adding it to the unselectedItems ID array and removing it from
   * the selectedItems ID array.
   */
  UNSELECT_ITEMS(state, { ids }) {
    // If selectedItems is passed in as ids, we must make a copy of the array, otherwise
    // it will update each time removeItem is envoked and break the forEach.
    const items = [...ids];
    const unselectedItemIds = state.features.unselectedItems.ids.slice();
    const selectedItemIds = state.features.selectedItems.ids.slice();

    items.forEach((id) => {
      addItem(unselectedItemIds, id);
      removeItem(selectedItemIds, id);
    });

    state.features.unselectedItems.ids = unselectedItemIds;
    state.features.selectedItems.ids = selectedItemIds;
  }

  UPDATE_ITEM(state, { id, attributes }) {
    const { boundItems, items } = state.entities;
    boundItems.byId[id] = merge({}, boundItems.byId[id], attributes);
    if (items.byId[id]) {
      items.byId[id] = merge({}, items.byId[id], attributes);
    }

    // Use array splice method to trigger a change so Vuex will update
    // Vuex will not update if it doesn't detect a change in items.ids
    // TODO we would like to remove this code
    const index = state.features.items.ids.indexOf(id);
    if (index > -1) {
      state.features.items.ids.splice(index, 1, id);
    }
  }

  // ----------------------------------------------------------------------------
  // Helper Methods
  // ----------------------------------------------------------------------------

  /**
   * When showing bound items the BoundItemsApi instance will be used instead
   * of the Api instance that was passed to the constructor. So the logic of
   * determining which Api instance to use is placed in this wrapper method
   * so that fetch() can be called on which ever instance happens to be in
   * use.
   */
  fetchWrapper(context, skip, limit, sort, searchTerm, filters, nextPageToken, skipToken) {
    if (context.state.features.showBoundItems) {
      const boundItems = context.state.entities.boundItems.byId;

      // TBD: Why do we need to check for a boundItemsUrl here? It seems like
      // that's superfluous. If there is no boundItemsUrl then we'll try to
      // fetch items rom the BoundItemsApi when it hasn't yet been populated.

      if (context.state.features.boundItemsUrl
          && !context.state.features.haveBoundItemsBeenFetched) {
        // If the bound items have not been initialized yet then initialize,
        // i.e. build the in memory dataset, and then fetch the requested
        // page of data.

        return new Promise((resolve, reject) => {
          this.initializeBoundItems(context)
            .then(() => {
              this.boundItemsApi
                .fetch(skip, limit, sort, searchTerm, boundItems)
                .then((items) => {
                  resolve(items);
                })
                .catch((error) => {
                  reject(error);
                });
            })
            .catch((error) => {
              reject(error);
            });
        });
      }

      // If there are bound items in the store then just return the promise
      // returned from the fetch() method.

      return this.boundItemsApi.fetch(skip, limit, sort, searchTerm, boundItems, filters);
    }

    // Finally, when not showing bound items then just return the promise
    // returned from the Api instance fetch() method.

    // If there is an ongoing fetch request, cancel it
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel();
    }

    // Create and save a cancel token for the fetch request
    this.cancelTokenSource = CancelToken.source();
    const cancelToken = this.cancelTokenSource.token;

    return this.Api.fetch(
      skip, limit, sort, searchTerm, cancelToken, filters, nextPageToken, skipToken,
    );
  }

  getApi(showBoundItems) {
    return showBoundItems ? this.boundItemsApi : this.Api;
  }

  /**
   * If the initial set of bound items, i.e. the in memory data set, hasn't
   * been populated then initialize the BoundItemsApi instance to get the
   * initial set of bound items, add them to the store, and then resolve a
   * promise so the caller can continue.
   * @param context Vuex context object
   * @param syncBoundItems If set to false then exit after setting the
   * previously bound items only. This is set to false when selecting or
   * unselecting all items.
   */
  initializeBoundItems(context, syncBoundItems = true) {
    return new Promise((resolve, reject) => {
      this.boundItemsApi
        .initialize(context.state.features.boundItemsUrl, this.Api.defaultParams)
        .then((items) => {
          if (!Array.isArray(items.results)) {
            resolve();
            return;
          }

          let ids = items.results.map(result => result.id);

          // Save the current IDs as the initial previously selected IDs so
          // that the initial state isn't corrupted by selections that may
          // have been made prior to navigating to the show bound items view.

          context.commit('BOUND_ITEMS_HAVE_BEEN_FETCHED');
          context.commit('SET_PREVIOUSLY_SELECTED_ITEMS', { ids });

          if (syncBoundItems === false) {
            resolve();
            return;
          }

          // Remove any bound items from the items.results that may have
          // been removed in the show all view prior to navigating to the
          // bound items view.

          for (let i = 0; i < items.results.length; i += 1) {
            const result = items.results[i];

            if (context.state.features.unselectedItems.ids.indexOf(result.id) !== -1) {
              items.results.splice(i, 1);
              items.totalCount -= 1;
              i -= 1;
            }
          }

          // Add any selected items that are not present in the bound items
          // returned from the server that may have been added in the show
          // all view prior to navigating to the show bound items view.

          context.state.features.selectedItems.ids.forEach((selectedItemId) => {
            let selectedItemFound = false;

            items.results.forEach((result) => {
              if (result.id === selectedItemId) {
                selectedItemFound = true;
              }
            });

            if (!selectedItemFound) {
              const item = context.state.entities.boundItems.byId[selectedItemId]
                || { id: selectedItemId };
              items.results.push(item);
              items.totalCount += 1;
            }
          });

          ids = items.results.map(result => result.id);

          context.commit('SET_BOUND_ITEMS', {
            results: items.results,
            totalCount: items.totalCount,
          });
          context.commit('SELECT_ITEMS', { ids });

          this.processBoundItems(context, items.results);

          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
